/**
 * Custom Options Encoder
 *
 * This module provides encoding and decoding functionality for custom FIDO2
 * authentication options into a 32-byte salt format.
 *
 * @module encoder
 */

/**
 * Custom authentication options for FIDO2 operations.
 *
 * These options are encoded into a 32-byte salt buffer that is passed
 * to the authenticator via the PRF (Pseudo-Random Function) extension.
 */
export interface CustomOptions {
  /**
   * Enable liveness detection check during authentication.
   * When true, the authenticator will verify that a live person is present.
   */
  checkLiveness?: boolean;

  /**
   * Enable age verification during authentication.
   * When true, the authenticator will verify the user's age.
   */
  checkAge?: boolean;

  /**
   * Enable age threshold verification during authentication.
   * When true, the authenticator will verify if the user's age is above
   * the specified threshold.
   *
   * Note: This should typically be used together with checkAge and a valid ageThreshold.
   */
  checkAgeAboveThreshold?: boolean;

  /**
   * Enable geolocation verification during authentication.
   * When true, the authenticator will verify the user's geographic location.
   */
  checkGeoLocation?: boolean;

  /**
   * The age threshold value (0-255) for age verification.
   *
   * This value is only used when checkAge and/or checkAgeAboveThreshold are enabled.
   *
   * @minimum 0
   * @maximum 255
   */
  ageThreshold?: number;
}

/**
 * Decoded custom options result with metadata.
 */
export interface DecodedCustomOptions extends CustomOptions {
  /**
   * The magic byte value (should always be 0xFF for valid encoded options)
   */
  magicByte: number;

  /**
   * The raw options flags byte
   */
  optionsFlags: number;

  /**
   * Whether the salt format is valid (magic byte is 0xFF)
   */
  isValid: boolean;
}

/**
 * Encodes custom authentication options into a 32-byte salt buffer.
 *
 * This function creates a standardized binary format for passing custom
 * authentication options to the FIDO2 authenticator through the PRF extension.
 *
 * **Salt Format Specification (32 bytes):**
 *
 * - **Byte 0**: Magic byte (0xFF) - Identifies this as a custom options salt
 * - **Byte 1**: Option flags (bitfield)
 *   - Bit 0 (0x01): CheckLiveness - Enable liveness detection
 *   - Bit 1 (0x02): CheckAge - Enable age verification
 *   - Bit 2 (0x04): CheckAgeAboveThreshold - Enable age threshold check
 *   - Bit 3 (0x08): CheckGeoLocation - Enable geolocation verification
 *   - Bits 4-7: Reserved for future use
 * - **Byte 2**: Age threshold (0-255) - The age value for verification
 * - **Bytes 3-31**: Reserved (all zeros) - Reserved for future extensions
 *
 * @param options - The custom authentication options to encode
 * @returns A 32-byte Uint8Array containing the encoded options
 *
 * @example
 * ```typescript
 * // Encode options with liveness and age checks
 * const salt = encodeCustomOptionsToSalt({
 *   checkLiveness: true,
 *   checkAge: true,
 *   checkAgeAboveThreshold: true,
 *   ageThreshold: 18
 * });
 *
 * // Use with WebAuthn PRF extension
 * const publicKeyOptions: PublicKeyCredentialRequestOptions = {
 *   challenge: new Uint8Array(32),
 *   extensions: {
 *     prf: {
 *       eval: { first: salt }
 *     }
 *   }
 * };
 * ```
 *
 * @throws {Error} If ageThreshold is provided and outside valid range (0-255)
 */
export function encodeCustomOptionsToSalt(options: CustomOptions = {}): Uint8Array {
  // Validate age threshold if provided
  if (options.ageThreshold !== undefined) {
    if (
      !Number.isInteger(options.ageThreshold) ||
      options.ageThreshold < 0 ||
      options.ageThreshold > 255
    ) {
      throw new Error('ageThreshold must be an integer between 0 and 255');
    }
  }

  // Create a 32-byte salt buffer (initialized to zeros)
  const salt = new Uint8Array(32);

  // Byte 0: Set magic byte to identify custom options format
  salt[0] = 0xff;

  // Byte 1: Build options flags bitfield
  let optionsFlags = 0;

  if (options.checkLiveness) {
    optionsFlags |= 1 << 0; // OptionFlag::CheckLiveness = 1 << 0
  }
  if (options.checkAge) {
    optionsFlags |= 1 << 1; // OptionFlag::CheckAge = 1 << 1
  }
  if (options.checkAgeAboveThreshold) {
    optionsFlags |= 1 << 2; // OptionFlag::CheckAgeAboveThreshold = 1 << 2
  }
  if (options.checkGeoLocation) {
    optionsFlags |= 1 << 3; // OptionFlag::CheckGeoLocation = 1 << 3
  }

  salt[1] = optionsFlags;

  // Byte 2: Set age threshold (default to 0 if not specified)
  salt[2] = (options.ageThreshold ?? 0) & 0xff;

  // Bytes 3-31 are already zero (default for Uint8Array)

  return salt;
}

/**
 * Decodes a 32-byte salt buffer back into custom authentication options.
 *
 * This function reverses the encoding process, extracting the custom options
 * from the binary salt format. It validates the magic byte and decodes all flags.
 *
 * @param salt - The 32-byte salt buffer to decode
 * @returns The decoded custom options with metadata
 *
 * @throws {Error} If salt is not exactly 32 bytes
 *
 * @example
 * ```typescript
 * const encoded = encodeCustomOptionsToSalt({ checkLiveness: true, ageThreshold: 18 });
 * const decoded = decodeCustomOptionsFromSalt(encoded);
 *
 * console.log(decoded.isValid); // true
 * console.log(decoded.checkLiveness); // true
 * console.log(decoded.ageThreshold); // 18
 * ```
 */
export function decodeCustomOptionsFromSalt(salt: Uint8Array): DecodedCustomOptions {
  // Validate salt length
  if (salt.length !== 32) {
    throw new Error('Salt must be exactly 32 bytes');
  }

  // Extract magic byte
  const magicByte = salt[0];
  const isValid = magicByte === 0xff;

  // Extract options flags
  const optionsFlags = salt[1];

  // Decode individual flags
  const checkLiveness = (optionsFlags & (1 << 0)) !== 0;
  const checkAge = (optionsFlags & (1 << 1)) !== 0;
  const checkAgeAboveThreshold = (optionsFlags & (1 << 2)) !== 0;
  const checkGeoLocation = (optionsFlags & (1 << 3)) !== 0;

  // Extract age threshold
  const ageThreshold = salt[2];

  return {
    magicByte,
    optionsFlags,
    isValid,
    checkLiveness,
    checkAge,
    checkAgeAboveThreshold,
    checkGeoLocation,
    ageThreshold,
  };
}

/**
 * Validates that a salt buffer has the correct custom options format.
 *
 * @param salt - The salt buffer to validate
 * @returns true if the salt is valid (correct length and magic byte)
 *
 * @example
 * ```typescript
 * const salt = new Uint8Array(32);
 * salt[0] = 0xFF;
 *
 * if (isValidCustomOptionsSalt(salt)) {
 *   // Safe to decode
 *   const options = decodeCustomOptionsFromSalt(salt);
 * }
 * ```
 */
export function isValidCustomOptionsSalt(salt: Uint8Array): boolean {
  return salt.length === 32 && salt[0] === 0xff;
}
